import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState, useRef } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import Head from "expo-router/head";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import ScButton from "@/src/components/ui-kit/button";
import { useTheme } from "@/src/styles/theme-provider";
import { publicUnsubscribeClient, validDigestToken } from "./unsubscribe";

type Status = "loading" | "ready" | "saving" | "confirmed" | "unavailable";
/** Public and outside AuthProvider: opening/scanning this page performs GET only. */
export default function DigestUnsubscribeScreen() {
  const params = useLocalSearchParams<{ token?: string; lang?: string }>();
  const token = typeof params.token === "string" ? params.token : "";
  const { t, i18n } = useTranslation();
  const { varStyle } = useTheme();
  const router = useRouter();
  const cache = useQueryClient();
  const [language, setLanguage] = useState(
    params.lang === "de-DE" ||
      i18n.language?.startsWith("de") ||
      (typeof navigator !== "undefined" && navigator.language?.startsWith("de"))
      ? "de-DE"
      : "en-US",
  );
  const [status, setStatus] = useState<Status>("loading");
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  const submitController = useRef<AbortController | null>(null);
  const label = (key: string) => t(key, { lng: language });
  useEffect(() => {
    const controller = new AbortController();
    const gen = ++generation.current;
    setStatus("loading");
    if (!validDigestToken(token)) {
      setStatus("unavailable");
      return () => controller.abort();
    }
    try {
      const client = publicUnsubscribeClient();
      void client
        .preview(token, controller.signal)
        .then(() => {
          if (gen === generation.current && !controller.signal.aborted)
            setStatus("ready");
        })
        .catch(() => {
          if (gen === generation.current && !controller.signal.aborted)
            setStatus("unavailable");
        });
    } catch {
      setStatus("unavailable");
    }
    return () => {
      if (generation.current === gen) generation.current = gen + 1;
      controller.abort();
      submitController.current?.abort();
    };
  }, [token, attempt]);
  const confirm = async () => {
    if (status !== "ready") return;
    setStatus("saving");
    const gen = generation.current,
      controller = new AbortController();
    submitController.current = controller;
    try {
      await publicUnsubscribeClient().confirm(token, controller.signal);
      // Mark private preference caches stale without starting authenticated
      // requests from this public page. Settings reconcile on the next visit.
      void cache.invalidateQueries({
        queryKey: ["following-v1"],
        refetchType: "none",
      });
      if (gen === generation.current && !controller.signal.aborted)
        setStatus("confirmed");
    } catch {
      if (gen === generation.current && !controller.signal.aborted)
        setStatus("unavailable");
    }
  };
  return (
    <View
      style={varStyle}
      className="flex-1 bg-obsidian items-center justify-center px-4 py-8"
    >
      <Head>
        <title>{label("FOLLOW_UNSUB_TITLE")}</title>
        <meta name="referrer" content="no-referrer" />
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <View className="w-full max-w-lg rounded-2xl border border-ash-3 bg-chalk p-6 gap-4">
        <View className="flex-row justify-end gap-2">
          <ScButton
            text="FOLLOW_LANGUAGE_EN"
            mode="text"
            onPress={() => setLanguage("en-US")}
            style={{ minHeight: 44 }}
          />
          <ScButton
            text="FOLLOW_LANGUAGE_DE"
            mode="text"
            onPress={() => setLanguage("de-DE")}
            style={{ minHeight: 44 }}
          />
        </View>
        <Text
          accessibilityRole="header"
          className="text-lg font-semibold text-onyx"
        >
          {label("FOLLOW_UNSUB_TITLE")}
        </Text>
        <Text className="text-sm text-slate">{label("FOLLOW_UNSUB_HINT")}</Text>
        <View accessibilityLiveRegion="polite">
          {(status === "loading" || status === "saving") && (
            <ActivityIndicator />
          )}
          {status === "confirmed" && (
            <Text className="text-onyx">{label("FOLLOW_UNSUB_DONE")}</Text>
          )}
          {status === "unavailable" && (
            <Text className="text-slate">{label("FOLLOW_UNSUB_ERROR")}</Text>
          )}
        </View>
        {status === "ready" && (
          <ScButton
            text={label("FOLLOW_UNSUB_CONFIRM")}
            style={{ minHeight: 44 }}
            onPress={() => {
              void confirm();
            }}
          />
        )}
        {status === "unavailable" && (
          <ScButton
            text={label("FOLLOW_UNSUB_RETRY")}
            mode="outlined"
            style={{ minHeight: 44 }}
            onPress={() => setAttempt((n) => n + 1)}
          />
        )}
        <ScButton
          text={label("FOLLOW_UNSUB_SETTINGS")}
          mode="text"
          style={{ minHeight: 44 }}
          onPress={() => router.push("/settings")}
        />
      </View>
    </View>
  );
}
